/**
 * Gestión de días libres — vista admin/lead.
 *
 * Diferencia con /days-off:
 *   - /days-off es la página del empleado (sus solicitudes + crear).
 *     Para admin/lead también muestra la lista de pendientes pero
 *     mezclada con su propio flujo.
 *   - /admin/days-off es la cola dedicada: filtros por estado, persona
 *     y rango; acciones inline; stats arriba. Pensada para revisión
 *     batch del equipo.
 */
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Calendar, Check, X, Search, ArrowLeft } from "lucide-react";
import type { DayOffRequest, SessionUser } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface RequestRow {
  request: DayOffRequest;
  requesterName: string;
  requesterEmail: string;
}

export const Route = createFileRoute("/admin/days-off")({
  component: AdminDaysOffPage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData<SessionUser>({
      queryKey: ["me"],
      queryFn: () => api.get<SessionUser>("/me"),
    });
    if (me.role !== "admin" && me.role !== "lead") {
      throw redirect({ to: "/" });
    }
  },
});

const KIND_LABEL: Record<DayOffRequest["kind"], string> = {
  vacation: "Vacaciones",
  sick: "Enfermedad",
  personal: "Personal",
  unpaid: "Sin sueldo",
};

type StatusFilter = "pending" | "approved" | "rejected" | "all";

function AdminDaysOffPage() {
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [search, setSearch] = useState("");
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });

  // 2 queries: pendientes (separadas, más comunes) + todas (filtro de
  // historia). Cada una con su key — TanStack las cachea aparte.
  const pendingQuery = useQuery<{ requests: RequestRow[] }>({
    queryKey: ["days-off", "pending"],
    queryFn: () => api.get("/days-off/pending"),
  });
  const allQuery = useQuery<{ requests: RequestRow[] }>({
    queryKey: ["days-off", "all"],
    queryFn: () => api.get("/days-off"),
  });

  const pendingRows = pendingQuery.data?.requests ?? [];
  const allRows = allQuery.data?.requests ?? [];

  const rows = useMemo(() => {
    let source: RequestRow[];
    if (status === "pending") source = pendingRows;
    else if (status === "all") source = allRows;
    else source = allRows.filter((r) => r.request.status === status);
    if (!search.trim()) return source;
    const q = search.trim().toLowerCase();
    return source.filter(
      (r) =>
        r.requesterName.toLowerCase().includes(q) || r.requesterEmail.toLowerCase().includes(q),
    );
  }, [status, pendingRows, allRows, search]);

  // Stats top: usamos allRows para approved/rejected del año actual y
  // pendingRows.length para el dato live de pendientes.
  const currentYear = new Date().getFullYear().toString();
  const approvedThisYear = allRows.filter(
    (r) => r.request.status === "approved" && r.request.startDate.startsWith(currentYear),
  ).length;
  const rejectedThisYear = allRows.filter(
    (r) => r.request.status === "rejected" && r.request.startDate.startsWith(currentYear),
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="administración"
        title="Días libres del equipo"
        subtitle="Aprobá o rechazá solicitudes. Solo admin/lead pueden ver esta vista."
        actions={
          <Link to="/days-off" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Vista personal
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Pendientes" value={pendingRows.length} tone="warning" />
        <StatCard label={`Aprobadas ${currentYear}`} value={approvedThisYear} tone="success" />
        <StatCard label={`Rechazadas ${currentYear}`} value={rejectedThisYear} tone="error" />
      </div>

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {(
            [
              { v: "pending", l: "Pendientes" },
              { v: "approved", l: "Aprobadas" },
              { v: "rejected", l: "Rechazadas" },
              { v: "all", l: "Todas" },
            ] as { v: StatusFilter; l: string }[]
          ).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setStatus(opt.v)}
              className={"btn btn-xs " + (status === opt.v ? "btn-primary" : "btn-ghost")}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <label className="input input-bordered input-sm flex items-center gap-2 max-w-xs flex-1 ml-auto">
          <Search className="size-3.5 text-base-content/40" />
          <input
            type="text"
            placeholder="Buscar por persona"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="grow text-xs"
          />
        </label>
      </div>

      {rows.length === 0 ? (
        <div className="card-elevated py-12 text-center text-sm text-base-content/55">
          {status === "pending"
            ? "Sin solicitudes pendientes."
            : "Nada que mostrar en este filtro."}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <AdminRow key={row.request.id} row={row} myUserId={me.data?.id} />
          ))}
        </ul>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "warning" | "success" | "error";
}) {
  const dot = { warning: "bg-warning", success: "bg-success", error: "bg-error" }[tone];
  return (
    <div className="card-elevated p-3.5">
      <div className="flex items-center gap-2 mb-1">
        <span className={`size-1.5 rounded-full ${dot}`} aria-hidden />
        <span className="text-[10px] uppercase tracking-wider text-base-content/55 font-medium">
          {label}
        </span>
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function AdminRow({ row, myUserId }: { row: RequestRow; myUserId?: string }) {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: (decision: { status: "approved" | "rejected"; approverComment?: string }) =>
      api.patch(`/days-off/${row.request.id}/decision`, decision),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["days-off"] }),
  });

  const range =
    row.request.startDate === row.request.endDate
      ? formatDate(row.request.startDate)
      : `${formatDate(row.request.startDate)} → ${formatDate(row.request.endDate)}`;

  const isPending = row.request.status === "pending";
  const isOwn = row.request.userId === myUserId;
  const statusBadge: Record<DayOffRequest["status"], string> = {
    pending: "badge-warning",
    approved: "badge-success",
    rejected: "badge-error",
    cancelled: "badge-ghost",
  };
  const statusLabel: Record<DayOffRequest["status"], string> = {
    pending: "Pendiente",
    approved: "Aprobada",
    rejected: "Rechazada",
    cancelled: "Cancelada",
  };

  return (
    <li className="card-elevated p-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="size-9 rounded-full bg-primary/15 grid place-items-center shrink-0 text-sm font-semibold text-primary">
          {row.requesterName
            .split(" ")
            .slice(0, 2)
            .map((s) => s[0])
            .join("")
            .toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{row.requesterName}</span>
            <span className="text-xs text-base-content/55">{row.requesterEmail}</span>
            <span className={`badge badge-xs ${statusBadge[row.request.status]}`}>
              {statusLabel[row.request.status]}
            </span>
          </div>
          <div className="text-sm mt-1 flex items-center gap-2 flex-wrap">
            <Calendar className="size-3.5 text-base-content/50" />
            <span>{range}</span>
            <span className="text-base-content/40">·</span>
            <span className="text-base-content/70">{KIND_LABEL[row.request.kind]}</span>
          </div>
          {row.request.reason && (
            <p className="text-xs text-base-content/65 mt-1.5 italic">"{row.request.reason}"</p>
          )}
          {row.request.approverComment && (
            <p className="text-xs text-base-content/55 mt-1.5">
              <strong className="text-base-content/75">Comentario:</strong>{" "}
              {row.request.approverComment}
            </p>
          )}
        </div>
        {isPending && !isOwn && (
          <div className="flex gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => decide.mutate({ status: "approved" })}
              disabled={decide.isPending}
              className="btn btn-success btn-sm gap-1.5"
              title="Aprobar"
            >
              <Check className="size-3.5" /> Aprobar
            </button>
            <button
              type="button"
              onClick={() => {
                const comment = window.prompt(
                  "Razón del rechazo (opcional):\n\nDejá vacío para rechazar sin nota.",
                );
                if (comment === null) return; // canceló
                decide.mutate({
                  status: "rejected",
                  approverComment: comment.trim() || undefined,
                });
              }}
              disabled={decide.isPending}
              className="btn btn-error btn-sm btn-outline gap-1.5"
              title="Rechazar"
            >
              <X className="size-3.5" /> Rechazar
            </button>
          </div>
        )}
        {isPending && isOwn && (
          <span className="text-xs text-base-content/45 italic shrink-0">
            (tu propia solicitud)
          </span>
        )}
      </div>
    </li>
  );
}
